import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { Artist, ArtistIndex, DeckArtist, PagedResult, ArtistSearchRequest, SubjectAggregate } from '../../models';

export interface IArtistService {

	page(pageIndex: number, pageSize: number): Promise<PagedResult<Artist>>;
	get(id: string): Promise<Artist>;
	getMultiple(ids: string[]): Promise<Artist[]>;
	autocomplete(crtieria: string): Promise<Artist[]>;
	index(): Promise<ArtistIndex[]>;
	search(request: ArtistSearchRequest): Promise<PagedResult<Artist>>;
	save(deck: Artist): Promise<Artist>;
	delete(id: string): Promise<any>;
	pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckArtist>>;
	listByDeck(deckId: string): Promise<DeckArtist[]>;
	listByCompany(companyId: string): Promise<Artist[]>;
	listByCompanyWithCount(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Artist>>>;
	pageByCompany(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<Artist>>;

}

@injectable()
export class ArtistService implements IArtistService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) {}


	public page(pageIndex: number, pageSize: number): Promise<PagedResult<Artist>> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Artist>>(url);
	}

	public get(id: string): Promise<Artist> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/${id}`;
		return this.httpClient.get<Artist>(url);
	}

	public getMultiple(ids: string[]): Promise<Artist[]> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<Artist[]>(url);
	}

	public autocomplete(criteria: string): Promise<Artist[]> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/autocomplete/${criteria}`;
		return this.httpClient.get<Artist[]>(url);
	}

	public index(): Promise<ArtistIndex[]> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/index`;
		return this.httpClient.get<ArtistIndex[]>(url);
	}

	public search(request: ArtistSearchRequest): Promise<PagedResult<Artist>> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/search`;
		return this.httpClient.post<PagedResult<Artist>>(url, request);
	}


	public save(model: Artist): Promise<Artist> {

		const payload: { [k: string]: any } = {
			displayName: model.displayName,
			firstName: model.firstName,
			middleName: model.middleName,
			lastName: model.lastName,
			nickName: model.nickName,
			birthDate: model.birthDate,
			aosId: (model.aosId != null) ? parseInt(model.aosId.toString(), null) : null,
			genderId: (model.genderId != null) ? parseInt(model.genderId.toString(), null) : null,
			birthYear: (model.birthYear != null) ? parseInt(model.birthYear.toString(), null) : null,
			birthMonth: (model.birthMonth != null) ? parseInt(model.birthMonth.toString(), null) : null,
			birthDay: (model.birthDay != null) ? parseInt(model.birthDay.toString(), null) : null,
			deathYear: (model.deathYear != null) ? parseInt(model.deathYear.toString(), null) : null,
			deathMonth: (model.deathMonth != null) ? parseInt(model.deathMonth.toString(), null) : null,
			deathDay: (model.deathDay != null) ? parseInt(model.deathDay.toString(), null) : null,
			description: model.description,
			name: model.gender != null ? model.gender.id : 0,
			hometown: model.hometown,
			isEnabled: model.isEnabled,
		};
		let url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}`;
		if (model.id !== null && model.id !== undefined && model.id !== '') {
			url += `/${model.id}`;
			return this.httpClient.put(url, payload);
		} else {
			return this.httpClient.post(url, payload);
		}
	}

	public delete(id: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/${id}`;
		return this.httpClient.delete(url);
	}


	public listByDeck(deckId: string): Promise<DeckArtist[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/artists`;
		return this.httpClient.get<DeckArtist[]>(url);
	}

	public pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckArtist>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/artists/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckArtist>>(url);
	}

	public listByCompany(companyId: string): Promise<Artist[]> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${companyId}/artists`;
		return this.httpClient.get<Artist[]>(url);
	}

	public listByCompanyWithCount(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Artist>>> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${companyId}/artists/with-count/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<SubjectAggregate<Artist>>>(url);
	}


	public pageByCompany(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<Artist>> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${companyId}/artists/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Artist>>(url);
	}



}
